import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MessageSquare, X } from "lucide-react";
import MessageThread from "@/components/MessageThread";

/**
 * Floating chat bubble for parents (and admins on non-admin routes).
 *
 * - Always visible bottom-right of the screen for parent users.
 * - Polls /messages/unread-count every 15s for the red badge.
 * - Click opens a slide-out panel anchored bottom-right with the thread.
 */
export default function MessagingBubble() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [threadId, setThreadId] = useState(null);
  const [emailOptIn, setEmailOptIn] = useState(true);

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await http.get("/messages/unread-count");
      setUnread(data.count || 0);
    } catch {
      // silent
    }
  }, [user]);

  // Lazily load thread + opt-in pref the first time the bubble opens
  const loadThread = useCallback(async () => {
    if (threadId) return;
    try {
      const { data } = await http.get("/messages/threads");
      if (data && data[0]) setThreadId(data[0].id);
      const me = await http.get("/auth/me");
      setEmailOptIn(me?.data?.user?.messaging_email_opt_in !== false);
    } catch {
      // silent
    }
  }, [threadId]);

  useEffect(() => {
    if (!user || user.role !== "parent") return;
    refreshUnread();
    const id = setInterval(refreshUnread, 15000);
    return () => clearInterval(id);
  }, [user, refreshUnread]);

  useEffect(() => {
    if (open) {
      loadThread();
      // Once opened, unread will be cleared by MessageThread's read call.
      setUnread(0);
    }
  }, [open, loadThread]);

  // Hide for non-parent users — admins have a dedicated /dashboard/admin/messages
  // page so they don't need the floating bubble.
  if (!user || user.role !== "parent") return null;

  return (
    <>
      {/* Bubble button */}
      <button
        onClick={() => setOpen((s) => !s)}
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-electric text-black shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
        aria-label={open ? "Close messages" : "Open messages"}
        data-testid="messaging-bubble"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
        {!open && unread > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] font-mono font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center"
            data-testid="messaging-bubble-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Slide-out panel */}
      {open && (
        <div
          className="fixed bottom-24 right-5 z-[59] w-[92vw] max-w-[400px] bento-card shadow-2xl overflow-hidden"
          data-testid="messaging-panel"
        >
          {threadId ? (
            <MessageThread
              threadId={threadId}
              otherName="VERIFIED Team"
              emailOptIn={emailOptIn}
              onEmailOptInChange={setEmailOptIn}
              height="500px"
            />
          ) : (
            <div className="h-[500px] flex items-center justify-center text-white/35 text-sm">
              Loading…
            </div>
          )}
        </div>
      )}
    </>
  );
}
